import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
} from '../../../../components/ui';
import { VEHICLE_CLASSES } from '../../../../data/master/equipments';
import { updateEquipment } from '../../../../repositories/equipment.repository';
import { lookupEquipmentTypes } from '../../../../repositories/lookup.repository';

function assetToForm(asset) {
  if (!asset) {
    return {
      equipmentType: '',
      equipmentDescription: '',
      remarks: '',
      vehicleClass: 'Construction Equipment',
      manufacturer: '',
      model: '',
      variant: '',
      capacity: '',
      capacityUom: '',
      yom: '',
      regNo: '',
      chassisNumber: '',
      engineNumber: '',
      dateOfReg: '',
      regValidUpto: '',
      nameAsPerRc: '',
      sonOfWifeOf: '',
      color: '',
      fuel: 'Diesel',
      pucDate: '',
      pucValidUpto: '',
      insuranceIssueDate: '',
      insuranceValidUpto: '',
      taxValidUpto: '',
      fitnessValidUpto: '',
    };
  }
  return {
    equipmentType: asset.equipmentType || '',
    equipmentDescription: asset.equipmentDescription || '',
    remarks: asset.remarks || '',
    vehicleClass: asset.vehicleClass || 'Construction Equipment',
    manufacturer: asset.manufacturer || '',
    model: asset.model || '',
    variant: asset.variant || '',
    capacity: asset.capacity || '',
    capacityUom: asset.capacityUom || '',
    yom: asset.yom || '',
    regNo: asset.regNo || '',
    chassisNumber: asset.chassisNumber || '',
    engineNumber: asset.engineNumber || '',
    dateOfReg: asset.dateOfReg || '',
    regValidUpto: asset.regValidUpto || '',
    nameAsPerRc: asset.nameAsPerRc || '',
    sonOfWifeOf: asset.sonOfWifeOf || '',
    color: asset.color || '',
    fuel: asset.fuel || 'Diesel',
    pucDate: asset.pucDate || '',
    pucValidUpto: asset.pucValidUpto || '',
    insuranceIssueDate: asset.insuranceIssueDate || '',
    insuranceValidUpto: asset.insuranceValidUpto || '',
    taxValidUpto: asset.taxValidUpto || '',
    fitnessValidUpto: asset.fitnessValidUpto || '',
  };
}

export function EquipmentEditModal({ isOpen, onClose, asset, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(() => assetToForm(asset));
  const [errors, setErrors] = useState({});
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(assetToForm(asset));
    setErrors({});
    let cancelled = false;
    (async () => {
      try {
        const items = await lookupEquipmentTypes();
        if (!cancelled) setEquipmentTypes(items || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load equipment types',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, asset, toast]);

  const equipmentOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({
          value: e.name,
          label: e.name,
          description: e.vehicleClass,
        })),
    [equipmentTypes],
  );

  const patch = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(partial).forEach((k) => delete next[k]);
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!asset?.id) return;

    const next = {};
    if (!form.equipmentType) next.equipmentType = 'Required';
    if (!form.manufacturer.trim()) next.manufacturer = 'Required';
    if (!form.regNo.trim()) next.regNo = 'Required';
    setErrors(next);
    if (Object.keys(next).length) {
      toast({ title: 'Fix highlighted fields', status: 'warning', position: 'top-right' });
      return;
    }

    const selectedType = equipmentTypes.find((eq) => eq.name === form.equipmentType);
    setSaving(true);
    try {
      const updated = await updateEquipment(asset.id, {
        ...asset,
        equipmentTypeId: selectedType?.id ?? asset.equipmentTypeId,
        equipmentType: form.equipmentType,
        equipmentDescription:
          form.equipmentDescription.trim() || form.equipmentType.trim(),
        remarks: form.remarks.trim(),
        vehicleClass: form.vehicleClass,
        manufacturer: form.manufacturer.trim(),
        model: form.model.trim(),
        variant: form.variant.trim(),
        capacity: form.capacity.trim(),
        capacityUom: form.capacityUom.trim(),
        yom: form.yom.trim(),
        regNo: form.regNo.trim().toUpperCase(),
        chassisNumber: form.chassisNumber.trim(),
        engineNumber: form.engineNumber.trim(),
        dateOfReg: form.dateOfReg,
        regValidUpto: form.regValidUpto,
        nameAsPerRc: form.nameAsPerRc.trim(),
        sonOfWifeOf: form.sonOfWifeOf.trim(),
        color: form.color.trim(),
        fuel: form.fuel.trim() || 'Diesel',
        pucDate: form.pucDate,
        pucValidUpto: form.pucValidUpto,
        insuranceIssueDate: form.insuranceIssueDate,
        insuranceValidUpto: form.insuranceValidUpto,
        taxValidUpto: form.taxValidUpto,
        fitnessValidUpto: form.fitnessValidUpto,
      });

      toast({
        title: 'Equipment updated',
        description: `${updated.assetId} · ${updated.equipmentDescription || updated.equipmentType}`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.(updated);
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update equipment',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!asset) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          Edit equipment · {asset.assetId}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" pb={4}>
          <VStack align="stretch" spacing={5}>
            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Vehicle basics
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppSelect
                  label="Equipment type"
                  isRequired
                  isSearchable
                  value={form.equipmentType}
                  onChange={(v) => {
                    const eq = equipmentTypes.find((e) => e.name === v);
                    patch({
                      equipmentType: v || '',
                      vehicleClass: eq?.vehicleClass || form.vehicleClass,
                    });
                  }}
                  options={equipmentOptions}
                  isInvalid={!!errors.equipmentType}
                  errorMessage={errors.equipmentType}
                />
                <AppSelect
                  label="Vehicle class"
                  value={form.vehicleClass}
                  onChange={(v) => patch({ vehicleClass: v })}
                  options={VEHICLE_CLASSES.map((c) => ({ value: c, label: c }))}
                />
                <AppInput
                  label="Equipment description"
                  value={form.equipmentDescription}
                  onChange={(e) => patch({ equipmentDescription: e.target.value })}
                  placeholder="e.g. WATER TANKER"
                />
                <AppInput
                  label="Make"
                  isRequired
                  value={form.manufacturer}
                  onChange={(e) => patch({ manufacturer: e.target.value })}
                  isInvalid={!!errors.manufacturer}
                  errorMessage={errors.manufacturer}
                />
                <AppInput
                  label="Model"
                  value={form.model}
                  onChange={(e) => patch({ model: e.target.value })}
                />
                <AppInput
                  label="Variant"
                  value={form.variant}
                  onChange={(e) => patch({ variant: e.target.value })}
                />
                <AppInput
                  label="Capacity"
                  value={form.capacity}
                  onChange={(e) => patch({ capacity: e.target.value })}
                />
                <AppInput
                  label="UOM"
                  value={form.capacityUom}
                  onChange={(e) => patch({ capacityUom: e.target.value })}
                  placeholder="T / Ltr. / CUM"
                />
                <AppInput
                  label="YOM"
                  value={form.yom}
                  onChange={(e) =>
                    patch({ yom: e.target.value.replace(/\D/g, '').slice(0, 4) })
                  }
                />
                <AppInput
                  label="Color"
                  value={form.color}
                  onChange={(e) => patch({ color: e.target.value })}
                />
                <AppInput
                  label="Fuel"
                  value={form.fuel}
                  onChange={(e) => patch({ fuel: e.target.value })}
                />
                <AppInput
                  label="Reg. / Sr. No."
                  isRequired
                  value={form.regNo}
                  onChange={(e) => patch({ regNo: e.target.value })}
                  isInvalid={!!errors.regNo}
                  errorMessage={errors.regNo}
                />
                <AppInput
                  label="Chassis no"
                  value={form.chassisNumber}
                  onChange={(e) => patch({ chassisNumber: e.target.value })}
                />
                <AppInput
                  label="Engine no"
                  value={form.engineNumber}
                  onChange={(e) => patch({ engineNumber: e.target.value })}
                />
                <AppDatePicker
                  label="Date of Reg."
                  value={form.dateOfReg}
                  onChange={(e) => patch({ dateOfReg: e.target.value })}
                />
                <AppDatePicker
                  label="Registration validity"
                  value={form.regValidUpto}
                  onChange={(e) => patch({ regValidUpto: e.target.value })}
                />
              </SimpleGrid>
              <Box mt={4}>
                <AppTextarea
                  label="Remarks"
                  value={form.remarks}
                  onChange={(e) => patch({ remarks: e.target.value })}
                  rows={3}
                />
              </Box>
            </Box>

            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Ownership (RC)
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppInput
                  label="Name as per RC"
                  value={form.nameAsPerRc}
                  onChange={(e) => patch({ nameAsPerRc: e.target.value })}
                />
                <AppInput
                  label="Son of / Wife of"
                  value={form.sonOfWifeOf}
                  onChange={(e) => patch({ sonOfWifeOf: e.target.value })}
                />
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Compliance dates
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppDatePicker
                  label="PUC date"
                  value={form.pucDate}
                  onChange={(e) => patch({ pucDate: e.target.value })}
                />
                <AppDatePicker
                  label="PUC valid up to"
                  value={form.pucValidUpto}
                  onChange={(e) => patch({ pucValidUpto: e.target.value })}
                />
                <AppDatePicker
                  label="Insurance issue date"
                  value={form.insuranceIssueDate}
                  onChange={(e) => patch({ insuranceIssueDate: e.target.value })}
                />
                <AppDatePicker
                  label="Insurance valid up to"
                  value={form.insuranceValidUpto}
                  onChange={(e) => patch({ insuranceValidUpto: e.target.value })}
                />
                <AppDatePicker
                  label="Fitness valid up to"
                  value={form.fitnessValidUpto}
                  onChange={(e) => patch({ fitnessValidUpto: e.target.value })}
                />
                <AppInput
                  label="RTO Tax valid up to"
                  value={form.taxValidUpto}
                  onChange={(e) => patch({ taxValidUpto: e.target.value })}
                  placeholder="Date or LTT"
                />
              </SimpleGrid>
            </Box>

            <Text fontSize="13px" color="text.secondary">
              Owner ({asset.ownerType}) and deploy status are not editable here — use KYC update
              for verification, and indents for deployment.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Save changes
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
